import React, { useEffect, useRef, useState } from 'react';
import { 
  FaGraduationCap, 
  FaTrophy, 
  FaCertificate, 
  FaCalendarAlt, 
  FaMapMarkerAlt,
  FaAward,
  FaBookOpen,
  FaLightbulb,
  FaChartLine,
  FaStar,
  FaSun,
  FaMoon
} from 'react-icons/fa';
import { SiGo, SiPython, SiLinux } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Education = () => {
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-timeline-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => {
      if (timelineRef.current) {
        observer.unobserve(timelineRef.current);
      }
    };
  }, []);

  const educationData = {
    degree: "Bachelor of Engineering in Computer Science",
    institution: "South East Asian College Of Engineering And Technology",
    location: "Bangalore, Karnataka, India",
    duration: "2020 – 2023",
    score: "8.5 CGPA",
    achievements: [
      {
        title: "Academic Excellence",
        description: "Consistently ranked among top 5% of the department",
        icon: <FaStar className="h-5 w-5" />,
        color: "from-yellow-500 to-orange-500",
        darkColor: "from-yellow-400 to-orange-400"
      },
      {
        title: "Technical Fest Representative",
        description: "Selected to represent University at National Level Technical Fest",
        icon: <FaTrophy className="h-5 w-5" />,
        color: "from-blue-500 to-cyan-500",
        darkColor: "from-blue-400 to-cyan-400"
      },
      {
        title: "IIM-B Vista 2019",
        description: "Youngest participant selected for prestigious business conclave with certification",
        icon: <FaCertificate className="h-5 w-5" />,
        color: "from-purple-500 to-pink-500",
        darkColor: "from-purple-400 to-pink-400"
      }
    ],
    skills: [
      { name: "Data Structures", level: 90, icon: "📊" },
      { name: "Algorithms", level: 85, icon: "⚡" },
      { name: "Operating Systems", level: 88, icon: "💻" },
      { name: "Database Systems", level: 90, icon: "🗄️" },
      { name: "Computer Networks", level: 85, icon: "🌐" },
      { name: "Software Engineering", level: 87, icon: "🏗️" },
    ],
    projects: [
      { name: "Cloud-based Attendance System", tech: "AWS, React, Node.js" },
      { name: "AI Chatbot for Student Queries", tech: "Python, NLP, Flask" },
      { name: "Library Management System", tech: "Java, MySQL, Spring Boot" },
    ]
  };

  return (
    <section 
      id="education" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      Theme Toggle Button
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/10 to-cyan-300/10'
        }`}></div>
        <div className={`absolute bottom-1/4 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/10 to-pink-300/10'
        }`}></div>
        
        {/* Floating icons */}
        <div className="absolute top-20 right-20 animate-float">
          <div className={`w-12 h-12 rounded-xl shadow-lg flex items-center justify-center backdrop-blur-sm ${
            darkMode 
              ? 'bg-gray-800/50' 
              : 'bg-white/80'
          }`}>
            <FaBookOpen className={`h-6 w-6 ${
              darkMode ? 'text-blue-400' : 'text-blue-600'
            }`} />
          </div>
        </div>
        <div className="absolute bottom-20 left-20 animate-float" style={{ animationDelay: '1s' }}>
          <div className={`w-12 h-12 rounded-xl shadow-lg flex items-center justify-center backdrop-blur-sm ${
            darkMode 
              ? 'bg-gray-800/50' 
              : 'bg-white/80'
          }`}>
            <FaLightbulb className={`h-6 w-6 ${
              darkMode ? 'text-yellow-400' : 'text-yellow-500'
            }`} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaGraduationCap className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">🎓</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Educational </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Journey
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Computer Science Graduate',
                2000,
                'Dean\'s List Student',
                2000,
                'Technical Fest Representative',
                2000,
                'Research Enthusiast',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Main Education Timeline */}
        <div ref={timelineRef} className="mb-16 opacity-0">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 via-cyan-500 to-purple-500 rounded-full"></div>
            
            {/* Degree Card */}
            <div className="relative mb-12 md:mb-16">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Year Badge */}
                <div className="relative z-10 flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center shadow-xl">
                    <div className="text-white text-center">
                      <div className="text-sm font-bold">2020</div>
                      <div className="text-xs">- 2023</div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur opacity-30 animate-pulse"></div>
                </div>
                
                {/* Content Card */}
                <div className="flex-1">
                  <div className={`relative rounded-2xl shadow-xl overflow-hidden backdrop-blur-sm border hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 ${
                    darkMode 
                      ? 'bg-gray-800/90 border-gray-700/50' 
                      : 'bg-white/90 border-gray-100'
                  }`}>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-500"></div>
                    
                    <div className="p-6 md:p-8">
                      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className={`text-2xl md:text-3xl font-bold mb-2 ${
                            darkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {educationData.degree}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 mb-4">
                            <div className={`flex items-center gap-2 font-medium ${
                              darkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              <FaGraduationCap />
                              <span>{educationData.institution}</span>
                            </div>
                            <div className={`flex items-center gap-2 ${
                              darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              <FaMapMarkerAlt />
                              <span>{educationData.location}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mb-6">
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                              darkMode 
                                ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 text-green-300' 
                                : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                            }`}>
                              <FaChartLine className={darkMode ? 'text-green-400' : 'text-green-600'} />
                              <span className="font-semibold">CGPA: {educationData.score}</span>
                            </div>
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                              darkMode 
                                ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300' 
                                : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
                            }`}>
                              <FaCalendarAlt className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                              <span className="font-semibold">{educationData.duration}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* CGPA Badge */}
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                              <div className="text-white text-center">
                                <div className="text-2xl font-bold">8.5</div>
                                <div className="text-xs">CGPA</div>
                              </div>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full blur opacity-30 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievements Section */}
            <div className="relative mb-16">
              <h3 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Academic <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Achievements</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {educationData.achievements.map((achievement, index) => (
                  <div
                    key={index}
                    className={`opacity-0 transform translate-y-8 group relative overflow-hidden backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${
                      darkMode 
                        ? 'bg-gray-800/50 border-gray-700' 
                        : 'bg-white/90 border-gray-100'
                    }`}
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${
                      darkMode ? achievement.darkColor : achievement.color
                    } opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                    
                    <div className={`relative mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br ${
                      darkMode ? achievement.darkColor : achievement.color
                    } text-white`}>
                      {achievement.icon}
                    </div>
                    
                    <h4 className={`text-xl font-bold mb-3 relative z-10 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{achievement.title}</h4>
                    <p className={`relative z-10 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>{achievement.description}</p>
                    
                    <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${
                      darkMode ? achievement.darkColor : achievement.color
                    } transition-all duration-500 group-hover:w-full`}></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical Skills Section */}
            <div className="mb-12">
              <h3 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Technical <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Skills Gained</span>
              </h3>
              
              <div className={`rounded-2xl p-6 md:p-8 backdrop-blur-sm border ${
                darkMode 
                  ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-gray-700/30' 
                  : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50 border-blue-100'
              }`}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {educationData.skills.map((skill, index) => (
                    <div key={index} className="group">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{skill.icon}</span>
                          <span className={`font-medium ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>{skill.name}</span>
                        </div>
                        <span className={`text-sm font-semibold ${
                          darkMode ? 'text-blue-400' : 'text-blue-600'
                        }`}>{skill.level}%</span>
                      </div>
                      <div className={`h-2 rounded-full overflow-hidden ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000"
                          style={{ 
                            width: `${skill.level}%`,
                            animationDelay: `${index * 100}ms`
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Academic Projects */}
            <div>
              <h3 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Academic <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Projects</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {educationData.projects.map((project, index) => (
                  <div
                    key={index}
                    className={`group relative overflow-hidden backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${
                      darkMode 
                        ? 'bg-gray-800/50 border-gray-700' 
                        : 'bg-white/90 border-gray-100'
                    }`}
                  >
                    <div className="absolute top-4 right-4">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        darkMode 
                          ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30' 
                          : 'bg-gradient-to-r from-blue-100 to-cyan-100'
                      }`}>
                        <FaAward className={`h-6 w-6 ${
                          darkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                      </div>
                    </div>
                    
                    <h4 className={`text-lg font-bold mb-3 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{project.name}</h4>
                    <p className={`text-sm mb-4 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Technologies:</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.split(', ').map((tech, i) => (
                        <span
                          key={i}
                          className={`px-3 py-1 text-xs rounded-full border ${
                            darkMode 
                              ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300 border-blue-700/30' 
                              : 'bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border-blue-200'
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    
                    <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 group-hover:w-full"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Callout */}
        <div className="text-center mt-12">
          <div className={`inline-flex items-center gap-3 px-6 py-4 rounded-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-blue-200/30'
          }`}>
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center">
              <FaLightbulb className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <p className={`text-lg font-semibold ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>
                Strong academic foundation combined with practical implementation
              </p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Developed critical problem-solving skills through hands-on projects and research
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes timeline-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-timeline-in {
          animation: timeline-in 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Education;